import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  Clock3,
  Globe,
  Image as ImageIcon,
  KeyRound,
  LifeBuoy,
  Palette,
  RefreshCw,
  Save,
  ServerCog,
  ShieldAlert,
  Sparkles,
  TestTube2,
  ToggleLeft,
  Users
} from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import "../../styles/UtopiaPages.css";
import { getPlanDefaults, MODULE_KEYS, MODULE_LABELS, normalizeModuleFlags } from "../../utils/moduleAccess";

type FeatureFlags = Record<string, boolean>;
const SCHOOL_SECTIONS = ["Nursery", "Primary", "Secondary"] as const;
type SchoolSection = (typeof SCHOOL_SECTIONS)[number];

interface AdminUser {
  id: number;
  name: string;
  email: string;
  role: string;
  phone?: string | null;
  last_login?: string | null;
}

interface SchoolPayload {
  id: number;
  name: string;
  subdomain: string;
  billing_email: string;
  status: string;
  subscription_tier: string;
  is_subscription_exempt?: number;
  max_students: number;
  max_teachers: number;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  settings?: Record<string, any>;
  metrics?: Record<string, number>;
  admins?: AdminUser[];
  feature_flags?: FeatureFlags;
  subscription?: { id: number; status: string; current_period_end?: string | null } | null;
  lifecycle?: { created_at?: string | null; updated_at?: string | null; subscription_expiry?: string | null };
  deployment?: Record<string, string | null>;
  audit_logs?: Array<{ id: number; action: string; entity_type?: string | null; user_email?: string | null; created_at?: string | null }>;
}

interface FormState {
  name: string;
  subdomain: string;
  billingEmail: string;
  schoolPhone: string;
  mainEmail: string;
  address: string;
  city: string;
  region: string;
  country: string;
  postalAddress: string;
  subscriptionTier: string;
  status: string;
  isSubscriptionExempt: boolean;
  subscriptionExpiryDate: string;
  maxStudents: number;
  maxTeachers: number;
  maxAdmins: number;
  maxHeadteachers: number;
  storageLimitMb: number;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  tagline: string;
  motto: string;
  schoolSections: SchoolSection[];
  academicYearStart: string;
  academicYearEnd: string;
  activeTerm: string;
  numberOfTerms: number;
  gradingSystem: string;
  reportCardTemplate: string;
  deploymentStatus: string;
  superadminNotes: string;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  adminTitle: string;
}

const moduleOptions = [
  ...MODULE_KEYS.map((key) => [key, MODULE_LABELS[key]])
] as const;

const blankForm = (): FormState => ({
  name: "",
  subdomain: "",
  billingEmail: "",
  schoolPhone: "",
  mainEmail: "",
  address: "",
  city: "",
  region: "",
  country: "",
  postalAddress: "",
  subscriptionTier: "basic",
  status: "trial",
  isSubscriptionExempt: false,
  subscriptionExpiryDate: "",
  maxStudents: 100,
  maxTeachers: 10,
  maxAdmins: 2,
  maxHeadteachers: 1,
  storageLimitMb: 2048,
  logoUrl: "",
  faviconUrl: "",
  primaryColor: "#0f766e",
  secondaryColor: "#0f172a",
  accentColor: "#f59e0b",
  tagline: "",
  motto: "",
  schoolSections: ["Primary"],
  academicYearStart: "",
  academicYearEnd: "",
  activeTerm: "Term 1",
  numberOfTerms: 3,
  gradingSystem: "Percentage",
  reportCardTemplate: "Standard",
  deploymentStatus: "active",
  superadminNotes: "",
  adminName: "",
  adminEmail: "",
  adminPassword: "",
  adminTitle: "Mr"
});

const dateOnly = (value?: string | null) => (value ? String(value).slice(0, 10) : "");
const when = (value?: string | null) => (value ? new Date(value).toLocaleString() : "Not available");
const parseSchoolSections = (value: any): SchoolSection[] => {
  const source = value?.school_sections ?? value?.schoolSections ?? value?.school_type ?? value?.schoolType ?? value;
  const values = Array.isArray(source)
    ? source
    : typeof source === "string"
      ? source.split(/[,/+&]/).map((item) => item.trim()).filter(Boolean)
      : [];

  const picked = new Set<SchoolSection>();
  values.forEach((item) => {
    const compact = String(item).toLowerCase().replace(/[\s_-]/g, "");
    if (compact === "combined" || compact === "all") {
      SCHOOL_SECTIONS.forEach((section) => picked.add(section));
      return;
    }
    const match = SCHOOL_SECTIONS.find((section) => section.toLowerCase() === compact);
    if (match) picked.add(match);
  });
  return SCHOOL_SECTIONS.filter((section) => picked.has(section));
};
const formatSchoolSections = (sections: SchoolSection[]) => sections.join(", ");

const Section = ({
  title,
  icon: Icon,
  subtitle,
  children
}: {
  title: string;
  icon: any;
  subtitle: string;
  children: React.ReactNode;
}) => (
  <section className="bg-white rounded-[1.75rem] border border-slate-200 shadow-sm p-6">
    <div className="mb-5">
      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Icon size={18} className="text-teal-600" />{title}</h2>
      <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
    </div>
    {children}
  </section>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.18em] font-bold text-slate-600">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const SchoolForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem("token") || "";
  const isEditMode = Boolean(id);
  const isBootstrapMode = searchParams.get("bootstrap") === "1";
  const justCreated = searchParams.get("created") === "1";
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [form, setForm] = useState<FormState>(blankForm);
  const [school, setSchool] = useState<SchoolPayload | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({});
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const hydrate = (payload: SchoolPayload) => {
    const settings = payload.settings || {};
    const parsedSections = parseSchoolSections(settings);
    const schoolSections: SchoolSection[] = parsedSections.length ? parsedSections : ["Primary"];
    setSchool(payload);
    setFeatureFlags(normalizeModuleFlags(payload.feature_flags || {}));
    setForm({
      name: payload.name || "",
      subdomain: payload.subdomain || "",
      billingEmail: payload.billing_email || "",
      schoolPhone: settings.phone || "",
      mainEmail: settings.main_email || payload.billing_email || "",
      address: settings.address || "",
      city: settings.city || "",
      region: settings.region || "",
      country: settings.country || "",
      postalAddress: settings.postal_address || "",
      subscriptionTier: payload.subscription_tier || "basic",
      status: payload.status || "trial",
      isSubscriptionExempt: Boolean(payload.is_subscription_exempt),
      subscriptionExpiryDate: dateOnly(payload.lifecycle?.subscription_expiry || payload.subscription?.current_period_end),
      maxStudents: Number(payload.max_students ?? 0),
      maxTeachers: Number(payload.max_teachers ?? 0),
      maxAdmins: Number(settings.max_admins ?? 2),
      maxHeadteachers: Number(settings.max_headteachers ?? 1),
      storageLimitMb: Number(settings.storage_limit_mb ?? 2048),
      logoUrl: payload.logo_url || "",
      faviconUrl: settings.favicon_url || "",
      primaryColor: payload.primary_color || "#0f766e",
      secondaryColor: payload.secondary_color || "#0f172a",
      accentColor: settings.accent_color || "#f59e0b",
      tagline: settings.tagline || "",
      motto: settings.motto || "",
      schoolSections,
      academicYearStart: dateOnly(settings.academic_year_start),
      academicYearEnd: dateOnly(settings.academic_year_end),
      activeTerm: settings.active_term || "Term 1",
      numberOfTerms: Number(settings.number_of_terms ?? 3),
      gradingSystem: settings.grading_system || "Percentage",
      reportCardTemplate: settings.report_card_template || "Standard",
      deploymentStatus: payload.deployment?.deploymentStatus || "active",
      superadminNotes: settings.superadmin_notes || "",
      adminName: "",
      adminEmail: "",
      adminPassword: "",
      adminTitle: "Mr"
    });
  };

  const fetchSchool = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError("");
      const response = await axios.get(getApiUrl(`superadmin/schools/${id}`), { headers });
      hydrate(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load school configuration");
    } finally {
      setLoading(false);
    }
  }, [headers, id]);

  useEffect(() => {
    if (isEditMode) fetchSchool();
  }, [fetchSchool, isEditMode]);

  useEffect(() => {
    if (justCreated) {
      setMessage("First school created. Review the defaults below, then keep tuning modules, branding, and deployment settings.");
    }
  }, [justCreated]);

  const validate = () => {
    if (!form.name.trim()) return "School name is required";
    if (!/^[a-z0-9-]+$/i.test(form.subdomain.trim())) return "Alias / subdomain may only contain letters, numbers, and hyphens";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.billingEmail.trim())) return "Billing email is invalid";
    if (form.schoolSections.length === 0) return "At least one school section must be selected";
    if ([form.maxStudents, form.maxTeachers, form.maxAdmins, form.maxHeadteachers, form.storageLimitMb].some((value) => Number.isNaN(value) || value < 0)) {
      return "Quota values cannot be negative";
    }
    if (!isEditMode && (!form.adminName.trim() || !form.adminEmail.trim() || !form.adminPassword.trim())) {
      return "Initial school administrator details are required";
    }
    return "";
  };

  const buildPayload = (deploy = false) => ({
    name: form.name.trim(),
    subdomain: form.subdomain.trim().toLowerCase(),
    billing_email: form.billingEmail.trim(),
    subscription_tier: form.subscriptionTier,
    status: form.status,
    max_students: Number(form.maxStudents),
    max_teachers: Number(form.maxTeachers),
    is_subscription_exempt: form.isSubscriptionExempt ? 1 : 0,
    subscriptionExpiryDate: form.subscriptionExpiryDate || undefined,
    logoUrl: form.logoUrl || null,
    faviconUrl: form.faviconUrl || null,
    primaryColor: form.primaryColor,
    secondaryColor: form.secondaryColor,
    accentColor: form.accentColor,
    phone: form.schoolPhone || null,
    mainEmail: form.mainEmail || null,
    address: form.address || null,
    city: form.city || null,
    region: form.region || null,
    country: form.country || null,
    postalAddress: form.postalAddress || null,
    tagline: form.tagline || null,
    motto: form.motto || null,
    schoolSections: form.schoolSections,
    academicYearStart: form.academicYearStart || null,
    academicYearEnd: form.academicYearEnd || null,
    activeTerm: form.activeTerm || null,
    numberOfTerms: Number(form.numberOfTerms),
    gradingSystem: form.gradingSystem || null,
    reportCardTemplate: form.reportCardTemplate || null,
    maxAdmins: Number(form.maxAdmins),
    maxHeadteachers: Number(form.maxHeadteachers),
    storageLimitMb: Number(form.storageLimitMb),
    deploymentStatus: deploy ? "provisioning" : form.deploymentStatus,
    superadminNotes: form.superadminNotes || null,
    admin_name: form.adminName.trim(),
    admin_email: form.adminEmail.trim(),
    admin_password: form.adminPassword,
    admin_title: form.adminTitle
  });

  const save = async (deploy = false) => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMessage("");
      if (isEditMode && id) {
        await axios.patch(getApiUrl(`superadmin/schools/${id}`), buildPayload(deploy), { headers });
        await axios.put(getApiUrl(`superadmin/settings/feature-flags/${id}`), { flags: featureFlags }, { headers });
        await fetchSchool();
        setMessage(deploy ? "School configuration saved and deployment refresh requested." : "School configuration updated.");
      } else {
        const response = await axios.post(getApiUrl("superadmin/schools"), buildPayload(false), { headers });
        const schoolId = response.data?.school?.id;
        if (schoolId) {
          await axios.put(getApiUrl(`superadmin/settings/feature-flags/${schoolId}`), { flags: featureFlags }, { headers });
          navigate(isBootstrapMode ? `/superadmin/schools/${schoolId}/edit?bootstrap=1&created=1` : `/superadmin/schools/${schoolId}/edit`);
        } else {
          navigate("/superadmin/schools");
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save school");
    } finally {
      setSaving(false);
    }
  };

  const withActionError = async (action: () => Promise<void>) => {
    try {
      setError("");
      await action();
    } catch (err: any) {
      setError(err.response?.data?.message || "Action failed");
    }
  };

  const statusChange = async (status: string) => {
    if (!id || !window.confirm(`Update school status to ${status}?`)) return;
    await withActionError(async () => {
      await axios.patch(getApiUrl(`superadmin/schools/${id}`), { status }, { headers });
      await fetchSchool();
      setMessage(`School status updated to ${status}.`);
    });
  };

  const toggleExempt = async () => {
    if (!id || !window.confirm("Update subscription exemption for this school?")) return;
    await withActionError(async () => {
      await axios.patch(getApiUrl(`superadmin/schools/${id}`), { is_subscription_exempt: form.isSubscriptionExempt ? 0 : 1 }, { headers });
      await fetchSchool();
      setMessage("Subscription exemption updated.");
    });
  };

  const extendExpiry = async () => {
    if (!id) return;
    const nextDate = window.prompt("Enter a new subscription expiry date (YYYY-MM-DD)", form.subscriptionExpiryDate || "");
    if (!nextDate) return;
    await withActionError(async () => {
      if (school?.subscription?.id) {
        await axios.patch(getApiUrl(`superadmin/subscriptions/${school.subscription.id}`), { current_period_end: nextDate }, { headers });
      } else {
        await axios.patch(getApiUrl(`superadmin/schools/${id}`), { subscriptionExpiryDate: nextDate }, { headers });
      }
      await fetchSchool();
      setMessage("Subscription expiry updated.");
    });
  };

  const resetPassword = async (admin: AdminUser) => {
    if (!id) return;
    const password = window.prompt(`Enter a new password for ${admin.name}`);
    if (!password) return;
    await withActionError(async () => {
      await axios.patch(getApiUrl(`superadmin/schools/${id}/admins/${admin.id}`), { password }, { headers });
      setMessage(`Credentials reset for ${admin.name}.`);
    });
  };

  const onFile = (key: "logoUrl" | "faviconUrl", file?: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => update(key, typeof reader.result === "string" ? reader.result : "");
    reader.readAsDataURL(file);
  };

  const metrics = school?.metrics || {};
  const portalUrl = school?.deployment?.portalUrl || (form.subdomain ? `https://${form.subdomain}.msportal.com` : "");
  const inputClass = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-[15px] font-medium text-slate-800 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-500/5 placeholder:text-slate-400";
  const backTarget = isEditMode && id ? `/superadmin/schools/${id}` : (isBootstrapMode ? "/superadmin" : "/superadmin/schools");
  const pageTitle = isEditMode
    ? "School Edit & Deployment Control"
    : isBootstrapMode
      ? "Bootstrap First School"
      : "Create New School";
  const pageSubtitle = isEditMode
    ? "Configure tenant identity, lifecycle, modules, branding, academic defaults, quotas, and deployment readiness from one control center."
    : isBootstrapMode
      ? "Provision the first tenant, initial school administrator, and baseline portal settings for this clean installation."
      : "Configure tenant identity, lifecycle, modules, branding, academic defaults, quotas, and deployment readiness from one control center.";
  const primaryActionLabel = isEditMode
    ? "Save changes"
    : isBootstrapMode
      ? "Create first school"
      : "Create school";

  if (loading) {
    return (
      <div className="flex items-center justify-center min-vh-100 bg-white">
        <div className="w-12 h-12 border-4 border-teal-500/20 border-t-teal-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="dashboard-main p-6 md:p-8 bg-slate-50/40">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6 mb-8">
            <div>
              <button type="button" onClick={() => navigate(backTarget)} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4">
                <ArrowLeft size={16} />Back to schools
              </button>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-950">{pageTitle}</h1>
              <p className="text-slate-500 mt-2 max-w-3xl">{pageSubtitle}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => navigate(backTarget)} className="px-5 py-3 rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-700">Cancel</button>
              <button type="button" onClick={() => { update("logoUrl", ""); update("faviconUrl", ""); update("primaryColor", "#0f766e"); update("secondaryColor", "#0f172a"); update("accentColor", "#f59e0b"); }} className="px-5 py-3 rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-700">Reset branding</button>
              {isEditMode && portalUrl ? <button type="button" onClick={() => window.open(portalUrl, "_blank", "noopener,noreferrer")} className="px-5 py-3 rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-700">View school portal</button> : null}
              <button type="button" onClick={() => save(false)} disabled={saving} className="px-5 py-3 rounded-2xl bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"><Save size={16} />{primaryActionLabel}</button>
              {isEditMode ? <button type="button" onClick={() => save(true)} disabled={saving} className="px-5 py-3 rounded-2xl bg-teal-600 text-white text-sm font-semibold disabled:opacity-60">Save and deploy</button> : null}
            </div>
          </div>

          {error ? <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-semibold text-rose-700">{error}</div> : null}
          {message ? <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-semibold text-emerald-700">{message}</div> : null}
          {isBootstrapMode && !isEditMode ? (
            <div className="mb-6 rounded-[1.75rem] border border-teal-200 bg-teal-50 px-5 py-4 text-sm text-teal-900">
              <div className="text-[11px] font-black uppercase tracking-[0.22em] text-teal-700">Bootstrap Mode</div>
              <p className="mt-2 font-semibold">Create the first school and its initial admin account. This becomes the platform's first live tenant.</p>
            </div>
          ) : null}

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.6fr)_390px] gap-6 items-start">
            <div className="space-y-6">
              <Section title="Core Identity" icon={Building2} subtitle="Primary institute identity, billing ownership, and location details.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="School name"><input className={inputClass} value={form.name} onChange={(e) => update("name", e.target.value)} /></Field>
                  <Field label="Alias / subdomain"><input className={inputClass} value={form.subdomain} onChange={(e) => update("subdomain", e.target.value.toLowerCase())} /></Field>
                  <Field label="Billing email"><input className={inputClass} type="email" value={form.billingEmail} onChange={(e) => update("billingEmail", e.target.value)} /></Field>
                  <Field label="Main school email"><input className={inputClass} type="email" value={form.mainEmail} onChange={(e) => update("mainEmail", e.target.value)} /></Field>
                  <Field label="School phone number"><input className={inputClass} value={form.schoolPhone} onChange={(e) => update("schoolPhone", e.target.value)} /></Field>
                  <Field label="Country"><input className={inputClass} value={form.country} onChange={(e) => update("country", e.target.value)} /></Field>
                  <Field label="Physical address"><input className={inputClass} value={form.address} onChange={(e) => update("address", e.target.value)} /></Field>
                  <Field label="City / town"><input className={inputClass} value={form.city} onChange={(e) => update("city", e.target.value)} /></Field>
                  <Field label="Province / region"><input className={inputClass} value={form.region} onChange={(e) => update("region", e.target.value)} /></Field>
                  <Field label="Postal address"><input className={inputClass} value={form.postalAddress} onChange={(e) => update("postalAddress", e.target.value)} /></Field>
                  <Field label="School motto"><input className={inputClass} value={form.motto} onChange={(e) => update("motto", e.target.value)} /></Field>
                </div>
              </Section>

              <Section title="Branding & Visual Identity" icon={Palette} subtitle="Set school-level branding that flows through the tenant portal.">
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_320px] gap-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Primary color"><input className={`${inputClass} h-12`} type="color" value={form.primaryColor} onChange={(e) => update("primaryColor", e.target.value)} /></Field>
                    <Field label="Secondary color"><input className={`${inputClass} h-12`} type="color" value={form.secondaryColor} onChange={(e) => update("secondaryColor", e.target.value)} /></Field>
                    <Field label="Accent color"><input className={`${inputClass} h-12`} type="color" value={form.accentColor} onChange={(e) => update("accentColor", e.target.value)} /></Field>
                    <Field label="Tagline"><input className={inputClass} value={form.tagline} onChange={(e) => update("tagline", e.target.value)} /></Field>
                    <Field label="Logo URL"><input className={inputClass} value={form.logoUrl} onChange={(e) => update("logoUrl", e.target.value)} /></Field>
                    <Field label="Favicon URL"><input className={inputClass} value={form.faviconUrl} onChange={(e) => update("faviconUrl", e.target.value)} /></Field>
                    <Field label="Upload logo"><input className={inputClass} type="file" accept="image/*" onChange={(e) => onFile("logoUrl", e.target.files?.[0])} /></Field>
                    <Field label="Upload favicon"><input className={inputClass} type="file" accept="image/*" onChange={(e) => onFile("faviconUrl", e.target.files?.[0])} /></Field>
                  </div>
                  <div className="rounded-[1.5rem] border border-slate-200 overflow-hidden bg-slate-950 text-white">
                    <div className="p-5" style={{ background: `linear-gradient(135deg, ${form.secondaryColor}, ${form.primaryColor})` }}>
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-white/15 border border-white/20 overflow-hidden flex items-center justify-center">{form.logoUrl ? <img src={form.logoUrl} alt="Logo preview" className="w-full h-full object-cover" /> : <ImageIcon size={22} />}</div>
                        <div><h3 className="text-xl font-bold">{form.name || "School Name"}</h3><p className="text-sm text-white/80">{form.tagline || form.motto || "Academic excellence, configured."}</p></div>
                      </div>
                    </div>
                    <div className="p-5 bg-white text-slate-900"><div className="flex items-center justify-between rounded-2xl px-4 py-3" style={{ backgroundColor: `${form.primaryColor}15` }}><span className="font-semibold">Portal Accent</span><span className="w-10 h-10 rounded-full border border-white shadow-sm" style={{ backgroundColor: form.accentColor }}></span></div></div>
                  </div>
                </div>
              </Section>

              <Section title="Academic Configuration" icon={CalendarDays} subtitle="Define the school's academic structure and reporting defaults.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <Field label="School sections">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {SCHOOL_SECTIONS.map((section) => {
                          const active = form.schoolSections.includes(section);
                          return (
                            <button
                              key={section}
                              type="button"
                              onClick={() =>
                                update(
                                  "schoolSections",
                                  active
                                    ? form.schoolSections.filter((item) => item !== section)
                                    : [...form.schoolSections, section]
                                )
                              }
                              className={`rounded-2xl border px-4 py-4 text-left transition ${active ? "border-teal-300 bg-teal-50 text-teal-700" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                            >
                              <div className="text-sm font-semibold">{section}</div>
                              <div className="text-xs mt-1 opacity-70">{active ? "Included in school structure" : "Click to include section"}</div>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-slate-500 mt-3">Selected: {formatSchoolSections(form.schoolSections) || "None selected"}</p>
                    </Field>
                  </div>
                  <Field label="Active term"><input className={inputClass} value={form.activeTerm} onChange={(e) => update("activeTerm", e.target.value)} /></Field>
                  <Field label="Academic year start"><input className={inputClass} type="date" value={form.academicYearStart} onChange={(e) => update("academicYearStart", e.target.value)} /></Field>
                  <Field label="Academic year end"><input className={inputClass} type="date" value={form.academicYearEnd} onChange={(e) => update("academicYearEnd", e.target.value)} /></Field>
                  <Field label="Number of terms"><input className={inputClass} type="number" min={1} value={form.numberOfTerms} onChange={(e) => update("numberOfTerms", Number(e.target.value))} /></Field>
                  <Field label="Grading system"><input className={inputClass} value={form.gradingSystem} onChange={(e) => update("gradingSystem", e.target.value)} /></Field>
                  <Field label="Report card template"><input className={inputClass} value={form.reportCardTemplate} onChange={(e) => update("reportCardTemplate", e.target.value)} /></Field>
                </div>
              </Section>

              <Section title="Capacity & Quotas" icon={Users} subtitle="Track limits and available room before the tenant reaches capacity.">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
                  <Field label="Student capacity"><input className={inputClass} type="number" min={0} value={form.maxStudents} onChange={(e) => update("maxStudents", Number(e.target.value))} /></Field>
                  <Field label="Teacher slots"><input className={inputClass} type="number" min={0} value={form.maxTeachers} onChange={(e) => update("maxTeachers", Number(e.target.value))} /></Field>
                  <Field label="Admin slots"><input className={inputClass} type="number" min={0} value={form.maxAdmins} onChange={(e) => update("maxAdmins", Number(e.target.value))} /></Field>
                  <Field label="Headteacher slots"><input className={inputClass} type="number" min={0} value={form.maxHeadteachers} onChange={(e) => update("maxHeadteachers", Number(e.target.value))} /></Field>
                  <Field label="Storage limit (MB)"><input className={inputClass} type="number" min={0} value={form.storageLimitMb} onChange={(e) => update("storageLimitMb", Number(e.target.value))} /></Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ["Learners", Number(metrics.totalLearners || 0), form.maxStudents, "bg-teal-500"],
                    ["Teachers", Number(metrics.totalTeachers || 0), form.maxTeachers, "bg-indigo-500"],
                    ["Admins", Number(metrics.totalAdmins || 0), form.maxAdmins, "bg-amber-500"],
                    ["Headteachers", Number(metrics.totalHeadteachers || 0), form.maxHeadteachers, "bg-rose-500"]
                  ].map(([label, value, max, tone]) => {
                    const percentage = Math.min(100, Math.round((Number(value) / Math.max(Number(max), 1)) * 100));
                    return <div key={String(label)} className="rounded-2xl border border-slate-200 p-4"><div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-slate-700">{label}</span><span className="text-xs text-slate-400">{value} / {max}</span></div><div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden"><div className={String(tone)} style={{ width: `${percentage}%`, height: "100%" }}></div></div></div>;
                  })}
                </div>
              </Section>

              <Section title="Enabled Modules" icon={ToggleLeft} subtitle="Control which platform capabilities are available for this school tenant.">
                <div className="flex flex-wrap gap-3 mb-4">
                  <button type="button" onClick={() => setFeatureFlags(getPlanDefaults(form.subscriptionTier))} className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700">
                    Apply {form.subscriptionTier || "current"} package
                  </button>
                  <button type="button" onClick={() => setFeatureFlags(getPlanDefaults("basic"))} className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700">Apply Basic Package</button>
                  <button type="button" onClick={() => setFeatureFlags(getPlanDefaults("standard"))} className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700">Apply Standard Package</button>
                  <button type="button" onClick={() => setFeatureFlags(getPlanDefaults("premium"))} className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700">Apply Premium Package</button>
                </div>
                <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Current plan: <span className="font-semibold text-slate-900 capitalize">{form.subscriptionTier}</span>. Enabled modules: <span className="font-semibold text-slate-900">{Object.values(featureFlags).filter(Boolean).length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {moduleOptions.map(([key, label]) => (
                    <button type="button" key={key} onClick={() => setFeatureFlags((current) => ({ ...current, [key]: !current[key] }))} className={`rounded-2xl border p-4 text-left transition ${featureFlags[key] ? "border-teal-300 bg-teal-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                      <div className="flex items-center justify-between gap-3"><p className="font-semibold text-slate-800">{label}</p><span className={`text-xs font-bold uppercase tracking-[0.22em] ${featureFlags[key] ? "text-teal-700" : "text-slate-400"}`}>{featureFlags[key] ? "On" : "Off"}</span></div>
                    </button>
                  ))}
                </div>
              </Section>

              <Section title="Internal Notes" icon={LifeBuoy} subtitle="Superadmin-only operational notes, migration remarks, and subscription context.">
                <textarea className={`${inputClass} min-h-[180px] resize-y`} value={form.superadminNotes} onChange={(e) => update("superadminNotes", e.target.value)} placeholder="Use this area for private notes about migration state, billing arrangements, launch blockers, or enterprise support details." />
              </Section>

              {!isEditMode ? (
                <Section title="Initial School Administrator" icon={ShieldAlert} subtitle="Bootstrap the first school admin during tenant creation.">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Admin name"><input className={inputClass} value={form.adminName} onChange={(e) => update("adminName", e.target.value)} /></Field>
                    <Field label="Admin email"><input className={inputClass} type="email" value={form.adminEmail} onChange={(e) => update("adminEmail", e.target.value)} /></Field>
                    <Field label="Admin password"><input className={inputClass} type="password" value={form.adminPassword} onChange={(e) => update("adminPassword", e.target.value)} /></Field>
                    <Field label="Admin title"><select className={inputClass} value={form.adminTitle} onChange={(e) => update("adminTitle", e.target.value)}><option>Mr</option><option>Mrs</option><option>Ms</option><option>Dr</option><option>Sir</option><option>Madam</option></select></Field>
                  </div>
                </Section>
              ) : null}
            </div>

            <aside className="space-y-6">
              <Section title="Status & Lifecycle" icon={ShieldAlert} subtitle="Operational state, subscription posture, and lifecycle milestones.">
                <div className="space-y-4">
                  <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4"><div className="flex items-center justify-between"><span className="text-lg font-bold text-slate-900 capitalize">{form.status}</span><span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.24em] ${form.isSubscriptionExempt ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{form.isSubscriptionExempt ? "Exempt" : school?.subscription?.status || form.subscriptionTier}</span></div></div>
                  <div className="grid grid-cols-2 gap-3 text-sm text-slate-600">
                    <div className="rounded-2xl border border-slate-200 p-4"><p className="text-xs font-semibold text-slate-400 mb-1">Created</p><p>{when(school?.lifecycle?.created_at)}</p></div>
                    <div className="rounded-2xl border border-slate-200 p-4"><p className="text-xs font-semibold text-slate-400 mb-1">Updated</p><p>{when(school?.lifecycle?.updated_at)}</p></div>
                  </div>
                  <Field label="Subscription tier"><select className={inputClass} value={form.subscriptionTier} onChange={(e) => update("subscriptionTier", e.target.value)}><option value="trial">Trial</option><option value="basic">Basic</option><option value="paid">Paid</option><option value="enterprise">Enterprise</option></select></Field>
                  <Field label="Subscription expiry"><input className={inputClass} type="date" value={form.subscriptionExpiryDate} onChange={(e) => update("subscriptionExpiryDate", e.target.value)} /></Field>
                  {isEditMode ? <div className="grid grid-cols-2 gap-3"><button type="button" onClick={() => statusChange("active")} className="rounded-2xl bg-emerald-600 text-white px-4 py-3 text-sm font-semibold">Activate</button><button type="button" onClick={() => statusChange("suspended")} className="rounded-2xl bg-rose-600 text-white px-4 py-3 text-sm font-semibold">Suspend</button><button type="button" onClick={toggleExempt} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">{form.isSubscriptionExempt ? "Remove exemption" : "Mark exempt"}</button><button type="button" onClick={extendExpiry} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">Extend expiry</button></div> : null}
                </div>
              </Section>

              <Section title="School Administrators" icon={Users} subtitle="Current tenant admins and quick account controls.">
                <div className="space-y-3">
                  {(school?.admins || []).length === 0 ? <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">No admin accounts assigned yet.</div> : (school?.admins || []).map((admin) => (
                    <div key={admin.id} className="rounded-2xl border border-slate-200 p-4">
                      <div className="flex items-start justify-between gap-3"><div><p className="font-semibold text-slate-900">{admin.name}</p><p className="text-sm text-slate-500">{admin.email}</p><p className="text-xs text-slate-400 mt-1">{admin.phone || admin.role}</p></div><button type="button" onClick={() => navigate(`/superadmin/schools/${id}/admins/${admin.id}/edit`)} className="text-sm font-semibold text-teal-700">View</button></div>
                      <div className="flex flex-wrap gap-2 mt-4"><button type="button" onClick={() => navigate(`/superadmin/schools/${id}/admins/${admin.id}/edit`)} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">Open account</button><button type="button" onClick={() => resetPassword(admin)} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 inline-flex items-center gap-1"><KeyRound size={13} />Reset credentials</button></div>
                      <p className="text-xs text-slate-400 mt-3">Last login: {when(admin.last_login)}</p>
                    </div>
                  ))}
                  {isEditMode ? <button type="button" onClick={() => navigate(`/superadmin/schools/${id}/admins/new`)} className="w-full rounded-2xl border border-dashed border-teal-300 bg-teal-50 px-4 py-3 text-sm font-semibold text-teal-700">Replace / add admin</button> : null}
                </div>
              </Section>

              <Section title="Deployment & Technical" icon={ServerCog} subtitle="Provisioning, environment, sync state, and portal access.">
                <div className="space-y-4 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-slate-200 p-4"><p className="text-xs font-semibold text-slate-400 mb-1">Environment</p><p className="capitalize text-slate-800">{school?.deployment?.environment || "development"}</p></div>
                    <div className="rounded-2xl border border-slate-200 p-4"><p className="text-xs font-semibold text-slate-400 mb-1">Database</p><p className="capitalize text-slate-800">{school?.deployment?.databaseStatus || "connected"}</p></div>
                  </div>
                  <Field label="Deployment status"><input className={inputClass} value={form.deploymentStatus} onChange={(e) => update("deploymentStatus", e.target.value)} /></Field>
                  <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 space-y-2">
                    <div className="flex items-center justify-between"><span className="text-slate-500">Last deployment</span><span className="font-medium text-slate-800">{when(school?.deployment?.lastDeploymentAt)}</span></div>
                    <div className="flex items-center justify-between"><span className="text-slate-500">Last sync</span><span className="font-medium text-slate-800">{when(school?.deployment?.lastSyncAt)}</span></div>
                    <div className="flex items-center justify-between"><span className="text-slate-500">DNS / SSL</span><span className="font-medium text-slate-800">{school?.deployment?.dnsStatus || "pending"} / {school?.deployment?.sslStatus || "pending"}</span></div>
                    <div className="flex items-center justify-between"><span className="text-slate-500">Tenant URL</span><span className="font-medium text-slate-800 break-all text-right">{portalUrl || "Not available"}</span></div>
                  </div>
                  {isEditMode ? <div className="grid grid-cols-2 gap-3"><button type="button" onClick={fetchSchool} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 inline-flex items-center justify-center gap-2"><RefreshCw size={15} />Refresh</button><button type="button" onClick={() => save(true)} className="rounded-2xl bg-slate-900 text-white px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"><ServerCog size={15} />Re-run provisioning</button><button type="button" onClick={() => portalUrl && window.open(portalUrl, "_blank", "noopener,noreferrer")} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 inline-flex items-center justify-center gap-2"><TestTube2 size={15} />Test URL</button><button type="button" onClick={() => portalUrl && window.open(portalUrl, "_blank", "noopener,noreferrer")} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 inline-flex items-center justify-center gap-2"><Globe size={15} />Open portal</button></div> : null}
                </div>
              </Section>

              <Section title="Metrics Snapshot" icon={Sparkles} subtitle="Current tenant usage and platform activity.">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ["Learners", Number(metrics.totalLearners || 0)],
                    ["Teachers", Number(metrics.totalTeachers || 0)],
                    ["Admins", Number(metrics.totalAdmins || 0)],
                    ["Classes", Number(metrics.totalClasses || 0)],
                    ["Active Modules", Number(metrics.activeModules || 0)],
                    ["30d Activity", Number(metrics.recentActivityCount || 0)]
                  ].map(([label, value]) => <div key={String(label)} className="rounded-2xl border border-slate-200 p-4 bg-white"><p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">{label}</p><p className="text-2xl font-bold text-slate-900 mt-2">{value}</p></div>)}
                </div>
              </Section>

              <Section title="Audit & History" icon={Clock3} subtitle="Recent configuration changes for this school.">
                <div className="space-y-3">
                  {(school?.audit_logs || []).length === 0 ? <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">No recent audit events for this school.</div> : (school?.audit_logs || []).map((log) => <div key={log.id} className="rounded-2xl border border-slate-200 p-4"><p className="text-sm font-semibold text-slate-900">{log.action.replace(/_/g, " ")}</p><p className="text-xs text-slate-500 mt-1">{log.user_email || "System"} | {log.entity_type || "record"}</p><p className="text-xs text-slate-400 mt-2">{when(log.created_at)}</p></div>)}
                </div>
              </Section>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SchoolForm;
